/**
 * AIDIA crypto layer.
 *
 * AES-256-GCM with a key derived from the master password via PBKDF2-SHA256.
 * The derived key exists only in memory and is dropped on lock. Nothing in this
 * file touches the network — by the time anything leaves the browser it is
 * already ciphertext.
 */

const PBKDF2_ITERATIONS = 250_000;
const enc = new TextEncoder();
const dec = new TextDecoder();

let cryptoKey = null; // CryptoKey, in memory only while unlocked

// --- base64 helpers -------------------------------------------------------
// Chunked so a large vault doesn't blow the argument limit on String.fromCharCode.
export function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let out = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(out);
}

export const fromB64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

export const randomBytes = (len) => crypto.getRandomValues(new Uint8Array(len));

/** A URL-safe random identifier, used for Sync IDs and sync tokens. */
export const randomId = (bytes = 16) =>
  Array.from(randomBytes(bytes), (b) => b.toString(16).padStart(2, "0")).join("");

async function deriveKey(password, saltBytes) {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: saltBytes, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/** Encrypt any JSON-serialisable value -> { iv, data } as base64 strings. */
export async function encrypt(value, key = cryptoKey) {
  if (!key) throw new Error("Vault is locked");
  const iv = randomBytes(12);
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(JSON.stringify(value))
  );
  return { iv: toB64(iv), data: toB64(cipher) };
}

/** Decrypt { iv, data } back to the original value. Throws if the key is wrong. */
export async function decrypt(payload, key = cryptoKey) {
  if (!key) throw new Error("Vault is locked");
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromB64(payload.iv) },
    key,
    fromB64(payload.data)
  );
  return JSON.parse(dec.decode(plain));
}

/**
 * Create a new vault: fresh salt, derived key, and a verifier blob that later
 * unlocks check against. The verifier reveals nothing about the password.
 */
export async function setup(password) {
  const salt = randomBytes(16);
  const key = await deriveKey(password, salt);
  const verifier = await encrypt("AIDIA_OK", key);
  cryptoKey = key;
  return { salt: toB64(salt), verifier };
}

/** Unlock an existing vault. Returns true and retains the key on success. */
export async function unlock(password, saltB64, verifier) {
  try {
    const key = await deriveKey(password, fromB64(saltB64));
    if ((await decrypt(verifier, key)) !== "AIDIA_OK") return false;
    cryptoKey = key;
    return true;
  } catch {
    return false;
  }
}

export const lock = () => {
  cryptoKey = null;
};
export const isUnlocked = () => cryptoKey !== null;

/**
 * Check a password without disturbing the in-memory key — used to re-authenticate
 * before revealing a stored password.
 */
export async function verify(password, saltB64, verifier) {
  try {
    const key = await deriveKey(password, fromB64(saltB64));
    return (await decrypt(verifier, key)) === "AIDIA_OK";
  } catch {
    return false;
  }
}

// --- biometric key wrapping ----------------------------------------------
export const importAesKey = (rawBytes) =>
  crypto.subtle.importKey("raw", rawBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
export const encryptWith = (key, value) => encrypt(value, key);
export const decryptWith = (key, payload) => decrypt(payload, key);

/** Cryptographically strong generator for the password vault. */
export function generatePassword(length = 20) {
  const sets = {
    upper: "ABCDEFGHJKLMNPQRSTUVWXYZ", // no I/O — they read as 1/0
    lower: "abcdefghijkmnopqrstuvwxyz",
    digits: "23456789",
    symbols: "!@#$%^&*()-_=+[]{}",
  };
  const all = Object.values(sets).join("");
  const pick = (set) => set[randomBytes(1)[0] % set.length];

  // At least one of each class, then fill.
  const out = [pick(sets.upper), pick(sets.lower), pick(sets.digits), pick(sets.symbols)];
  for (let i = out.length; i < length; i++) out.push(pick(all));

  // Fisher–Yates, so the guaranteed characters aren't always in front.
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomBytes(1)[0] % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join("");
}

/** Rough strength read-out for the generator UI. */
export function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "Empty", tone: "muted" };
  let pool = 0;
  if (/[a-z]/.test(pw)) pool += 26;
  if (/[A-Z]/.test(pw)) pool += 26;
  if (/[0-9]/.test(pw)) pool += 10;
  if (/[^A-Za-z0-9]/.test(pw)) pool += 32;
  const bits = pw.length * Math.log2(pool || 1);
  if (bits < 40) return { score: 1, label: "Weak", tone: "danger" };
  if (bits < 60) return { score: 2, label: "Fair", tone: "warning" };
  if (bits < 90) return { score: 3, label: "Strong", tone: "success" };
  return { score: 4, label: "Very strong", tone: "success" };
}
